/**
 * Bark box + outcome feed + bark inspector as one block, for surfaces without the Lanes shell
 * (the project page today). The Lanes tab mounts the same components through its slots.
 */
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useWide } from '../useWide.js'
import { BarkBox } from './BarkBox.js'
import { BarkInspector } from './BarkInspector.js'
import { OutcomeFeed } from './OutcomeFeed.js'

export function BarkPanel({ projectId, projectName }: { projectId: string; projectName: string }) {
  const nav = useNavigate()
  const wide = useWide()
  const [open, setOpen] = useState<string | null>(null)
  const [full, setFull] = useState(false)
  const opener = useRef<HTMLElement | null>(null)
  const inspect = (key: string) => {
    const [kind, id, panel] = key.split(':')
    if (kind === 'bark' && id) {
      opener.current = document.activeElement as HTMLElement | null
      setOpen(panel ? `${id}:${panel}` : id)
    } else if (kind === 'barks') setFull(true)
    else if (kind === 'task' && id) void nav({ to: '/t/$taskId', params: { taskId: id } })
    else if (kind === 'run' && id) void nav({ to: '/s/$runId', params: { runId: id } })
  }
  const close = () => {
    setOpen(null)
    opener.current?.focus()
  }
  useEffect(() => {
    if (!open) return
    document.getElementById(`bark-h-${open.split(':')[0]}`)?.focus()
  }, [open])
  const [barkId, panel] = open?.split(':') ?? []
  const inspector = barkId ? (
    <BarkInspector
      key={barkId}
      projectId={projectId}
      barkId={barkId}
      onInspect={inspect}
      {...(panel === 'redirect' ? { initialPanel: 'redirect' as const } : {})}
    />
  ) : null
  return (
    <div className="grid gap-3 px-4 pb-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)]">
      <div className="grid content-start gap-3">
        <BarkBox
          projectId={projectId}
          projectName={projectName}
          onSubmitted={(id, mode) => (mode === 'discuss' ? inspect(`bark:${id}`) : undefined)}
        />
        <OutcomeFeed
          projectId={projectId}
          projectName={projectName}
          limit={full ? 200 : 5}
          defaultDensity={full ? 'expanded' : 'compact'}
          onInspect={inspect}
        />
      </div>
      {inspector && wide ? (
        <aside
          aria-label="Item details"
          className="relative max-h-[80vh] overflow-y-auto rounded-xl border border-border-subtle bg-surface-1 p-4"
        >
          <button
            type="button"
            aria-label="Close details"
            className="absolute top-3 right-3 text-text-3"
            onClick={close}
          >
            <X size={16} aria-hidden="true" />
          </button>
          {inspector}
        </aside>
      ) : null}
      {inspector && !wide ? (
        // Mobile: a full-screen push view with a back button (never a bottom sheet).
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Item details"
          className="fixed inset-0 z-50 overflow-y-auto bg-bg p-4"
        >
          <button
            type="button"
            className="mb-3 inline-flex items-center gap-1 text-[15px] text-accent-text"
            onClick={close}
          >
            <ArrowLeft size={16} aria-hidden="true" /> Back
          </button>
          {inspector}
        </div>
      ) : null}
    </div>
  )
}
